import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import { cleanSignupStaffEmails, signupPaidStaffCount, signupStaffLimit } from '../utils/posSignupUtil';

/**
 * Step two of a paid sign-up: who else will use the till. The landing page's
 * version of the till's Subscribe "Staff accounts" step.
 *
 * THE PLAN'S FREE ACCOUNTS COME FIRST. The first `plan.freeStaff` addresses
 * cost nothing; each one beyond is charged at the add-on price — the same
 * rule as a till's renewal. A plan that offers no add-ons stops at its free
 * accounts.
 *
 * Opens with as many rows as the visitor picked on the pricing card (free +
 * add-ons), so the number they chose there carries over. Empty rows are
 * ignored, and the step can be skipped by leaving every row empty.
 *
 * @param {object} props
 * @param {object} props.plan
 * @param {{extraStaffPrice: number}} props.prices
 * @param {string[]} props.value - the rows, as typed
 * @param {(rows: string[]) => void} props.onChange
 * @param {(staffEmails: string[]) => void} props.onContinue - called with cleaned addresses; runs inside the click
 * @param {() => void} props.onBack
 * @param {string} [props.error] - from the parent (e.g. an expired checkout)
 * @param {(message: string) => void} props.onError - reports a validation problem
 * @returns {JSX.Element}
 */
function PosLandingStaffStep({
  plan, prices, value, onChange, onContinue, onBack, error = '', onError,
}) {
  const { config } = useConfig();
  const free = plan.freeStaff || 0;
  const limit = signupStaffLimit(plan);
  const rows = value.length > 0 ? value : [''];
  const typed = rows.filter((row) => String(row).trim()).length;
  const paid = signupPaidStaffCount(plan, typed);
  const price = prices.extraStaffPrice;
  // "3-Months Plan" already says plan; "Monthly" needs the word.
  const planLabel = /plan$/i.test(plan.name.trim()) ? plan.name : `${plan.name} plan`;

  /**
   * Replaces one row.
   * @param {number} index
   * @param {string} next
   * @returns {void}
   */
  const setRow = (index, next) => {
    const copy = [...rows];
    copy[index] = next;
    onChange(copy);
    onError('');
  };

  /**
   * Removes one row, keeping at least one to type into.
   * @param {number} index
   * @returns {void}
   */
  const removeRow = (index) => {
    const copy = rows.filter((_, i) => i !== index);
    onChange(copy.length > 0 ? copy : ['']);
    onError('');
  };

  /**
   * Validates the addresses and moves on to checkout.
   * @param {React.FormEvent<HTMLFormElement>} event
   * @returns {void}
   */
  const handleContinue = (event) => {
    event.preventDefault();
    try {
      onContinue(cleanSignupStaffEmails(rows, plan));
    } catch (validationError) {
      onError(validationError.message);
    }
  };

  return (
    <form id="subForm" className="staff-step" noValidate onSubmit={handleContinue}>
      <h4 className="staff-title">Want additional staff accounts?</h4>
      <p className="privacy-note">
        {free > 0 && (
          <>
            Your {planLabel} includes <b>{free} free staff account{free === 1 ? '' : 's'}</b>.
            {plan.allowAddons && <> Each account beyond that is {formatMoney(price, config)} for the whole length of the plan.</>}
          </>
        )}
        {free === 0 && plan.allowAddons && (
          <>Each staff account is {formatMoney(price, config)} for the whole length of your {planLabel}.</>
        )}
        {' '}Enter the email address each person will sign in with, or leave this empty if you don&rsquo;t need any.
      </p>

      <div className="staff-list">
        {rows.map((row, index) => (
          // Keyed by index: plain strings with no id, and two rows can hold
          // the same text mid-edit.
          // eslint-disable-next-line react/no-array-index-key
          <div className="staff-row" key={index}>
            <input
              type="email"
              value={row}
              placeholder={index < free ? 'staff@yourbusiness.com (free)' : 'staff@yourbusiness.com'}
              onChange={(event) => setRow(index, event.target.value)}
              spellCheck="false"
              aria-label={`Staff email ${index + 1}`}
            />
            <button type="button" className="staff-remove" onClick={() => removeRow(index)} aria-label={`Remove staff email ${index + 1}`}>
              Remove
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="staff-add"
        onClick={() => onChange([...rows, ''])}
        disabled={rows.length >= limit}
      >
        + Add another staff account
      </button>
      <p className="staff-limit">Up to {limit} staff account{limit === 1 ? '' : 's'} on this plan.</p>

      <div className="staff-run">
        <span>{Math.min(typed, free) > 0 ? `${Math.min(typed, free)} free · ` : ''}{paid} × {formatMoney(price, config)}</span>
        <b className="money">{formatMoney(paid * price, config)}</b>
      </div>

      {error && <p className="pick-warn">{error}</p>}
      <button type="submit" className="btn btn-primary btn-lg">Proceed to checkout</button>
      <button type="button" className="btn btn-ghost btn-lg staff-back" onClick={onBack}>Back to details</button>
    </form>
  );
}

export default PosLandingStaffStep;
